'use client'

import { motion } from 'framer-motion'
import { cn } from '@/lib/utils'
import { Icons } from '@/components/ui/icons'
import { useAI } from '@/context/AIContext'

interface Capability {
  icon: React.ElementType
  label: string
  query: string
  accent?: string
}

/**
 * A suggestion set for one view.
 *
 * `name` is the human name of the page or sub-view. It is not decoration: the
 * "about this view" and "export" chips are generated from it, so a new section
 * only has to be named once rather than have its title copy-pasted into three
 * chip queries that then drift apart.
 */
interface CapabilitySet {
  name: string
  capabilities: Capability[]
}

// ── Capability sets, keyed the way the backend page registry is keyed ──────
//
// The key is `route` for a whole page and `route#section` for a sub-view. The
// section half matters because the Operations Hub sub-views are React state
// (`activeTab` in workbench/page.tsx), never the URL, so a chip set chosen on
// pathname alone showed Retention Radar prompts to someone reading Separations.
// `currentPageSection` on AIContext is what the tabs publish, and it is the same
// key the backend registry resolves against, so the chips and the AI Manager's
// own page context can never disagree about which view is open.

const CAPABILITY_SETS: Record<string, CapabilitySet> = {
  // ── HR Auto ─────────────────────────────────────────────────────────────
  //
  // These four explain the page rather than duplicate it. Anything the page can
  // start belongs in a scenario chip on the page itself, where one click runs it;
  // asking the copilot to describe a run it cannot start would be the worse half
  // of both surfaces.
  '/hr-auto': {
    name: 'HR Auto',
    capabilities: [
      { icon: Icons.sparkles,      label: 'What can HR Auto start?', query: 'What can HR Auto start for me, and what happens in each case?', accent: 'text-brand-purple' },
      { icon: Icons.shield,        label: 'Policies on a hire',      query: 'Which policies govern a hiring request, and what does each one require before a role can be advertised?', accent: 'text-purple-600' },
      { icon: Icons.network,       label: 'Systems it touches',      query: 'Which systems does the Corporate Services coworker touch when it opens a vacancy, and what does it do in each one?', accent: 'text-brand-cornflower' },
      { icon: Icons.fileText,      label: 'Where the run lands',     query: 'Where does the record of an HR Auto run end up, and what does it capture about who approved what?', accent: 'text-brand-navy' },
    ],
  },

  // ── Command Briefing ────────────────────────────────────────────────────
  '/': {
    name: 'Command Briefing',
    capabilities: [
      { icon: Icons.activity,      label: 'Explain these KPIs',   query: 'Walk me through the headline KPIs on this dashboard and what each one is telling me right now.', accent: 'text-emerald-600' },
      { icon: Icons.alertTriangle, label: 'Flight risks',         query: 'Who are the top flight risks right now and what is driving each score?', accent: 'text-red-600' },
      { icon: Icons.inbox,         label: 'Hiring pipeline',      query: 'How is the hiring pipeline tracking? Show open requisitions by stage and urgency.', accent: 'text-brand-cornflower' },
      { icon: Icons.brain,         label: 'What needs me today',  query: 'What are the three things in the Corporate Services Command Center that need my attention today?', accent: 'text-brand-navy' },
    ],
  },

  // ── Operations Hub ──────────────────────────────────────────────────────
  '/workbench': {
    name: 'Operations Hub',
    capabilities: [
      { icon: Icons.alertTriangle, label: 'Top flight risks',     query: 'Who are the top flight risks right now?', accent: 'text-red-600' },
      { icon: Icons.inbox,         label: 'Open requisitions',    query: 'List all open requisitions and their urgency.', accent: 'text-brand-cornflower' },
      { icon: Icons.activity,      label: 'Onboarding issues',    query: 'Show me all onboarding SLA issues.', accent: 'text-amber-600' },
      { icon: Icons.barChart,      label: 'Department analytics', query: 'Give me department-level analytics for Engineering.', accent: 'text-brand-navy' },
    ],
  },
  '/workbench#requisitions': {
    name: 'Requisitions',
    capabilities: [
      { icon: Icons.inbox,         label: 'Open requisitions',    query: 'List every open requisition with its department, stage and urgency.', accent: 'text-brand-cornflower' },
      { icon: Icons.clock,         label: 'Slowest to fill',      query: 'Which requisitions have been open longest, and where in the funnel are they stuck?', accent: 'text-amber-600' },
      { icon: Icons.gitBranch,     label: 'Funnel drop-off',      query: 'Where is the biggest candidate drop-off in the requisition funnel?', accent: 'text-purple-600' },
      { icon: Icons.barChart,      label: 'Hiring by department', query: 'Break open requisitions down by department and show which teams are hiring hardest.', accent: 'text-brand-navy' },
    ],
  },
  '/workbench#onboarding': {
    name: 'Onboarding',
    capabilities: [
      { icon: Icons.userPlus,      label: 'New hires in flight',  query: 'Who is currently in onboarding and how far through their plan is each of them?', accent: 'text-emerald-600' },
      { icon: Icons.alertCircle,   label: 'Overdue tasks',        query: 'Show me every overdue onboarding task and who owns it.', accent: 'text-red-600' },
      { icon: Icons.clock,         label: 'Onboarding SLAs',      query: 'Which onboarding SLAs are at risk or already breached?', accent: 'text-amber-600' },
      { icon: Icons.shield,        label: 'Day-one readiness',    query: 'Which new hires are not day-one ready, and what is missing for each?', accent: 'text-brand-navy' },
    ],
  },
  '/workbench#my-team': {
    name: 'My Team',
    capabilities: [
      { icon: Icons.users,         label: 'Team health',          query: 'Give me a health read on my team: tenure, risk badges and anyone trending the wrong way.', accent: 'text-brand-cornflower' },
      { icon: Icons.brain,         label: 'Compliance gaps',      query: 'Show me all compliance gaps and overdue mandatory training on my team.', accent: 'text-purple-600' },
      { icon: Icons.trendingUp,    label: 'Performance spread',   query: 'How are performance ratings distributed across my team in the latest review cycle?', accent: 'text-emerald-600' },
      { icon: Icons.heart,         label: 'Engagement signals',   query: 'What do the latest engagement signals say about my team?', accent: 'text-red-600' },
    ],
  },
  '/workbench#retention-radar': {
    name: 'Retention Radar',
    capabilities: [
      { icon: Icons.alertTriangle, label: 'Who is at risk',       query: 'Who is on the Retention Radar right now, ranked by flight risk score?', accent: 'text-red-600' },
      { icon: Icons.brain,         label: 'Why they are at risk', query: 'For the highest flight-risk employees on this radar, what are the actual risk factors behind each score?', accent: 'text-purple-600' },
      { icon: Icons.building,      label: 'Risk by department',   query: 'Which departments carry the most retention risk, and how concentrated is it?', accent: 'text-brand-navy' },
      { icon: Icons.zap,           label: 'Intervention plan',    query: 'Recommend a retention intervention for each employee above the flight-risk threshold, using their own risk factors.', accent: 'text-emerald-600' },
    ],
  },
  '/workbench#separations': {
    name: 'Separations',
    capabilities: [
      { icon: Icons.userMinus,     label: 'Recent separations',   query: 'List recent separations with department, tenure at exit and separation type.', accent: 'text-red-600' },
      { icon: Icons.gitBranch,     label: 'Top exit reasons',     query: 'What are the top exit reasons, and how are voluntary and involuntary separations split?', accent: 'text-purple-600' },
      { icon: Icons.trendingUp,    label: 'Attrition rate',       query: 'What is our attrition rate, and which departments are above the company figure?', accent: 'text-amber-600' },
      { icon: Icons.clock,         label: 'Regretted attrition',  query: 'Which exits were regretted attrition, and what did those employees have in common?', accent: 'text-brand-navy' },
    ],
  },

  // ── HR Case Management ──────────────────────────────────────────────────
  '/hr-case-management': {
    name: 'Corporate Services Management',
    capabilities: [
      { icon: Icons.gitBranch,     label: 'Classify L1 or L2',    query: 'An employee wants to check their PTO balance. Is this L1 or L2?', accent: 'text-brand-cornflower' },
      { icon: Icons.alertCircle,   label: 'Auto Workbench',       query: 'What is in the Auto Workbench right now?', accent: 'text-amber-600' },
      { icon: Icons.inbox,         label: 'SLA breaches',         query: 'Show me all cases with breached SLAs.', accent: 'text-red-600' },
      { icon: Icons.shield,        label: 'Route this case',      query: 'An employee is requesting a reasonable accommodation for a disability. How does this get routed?', accent: 'text-purple-600' },
    ],
  },
  '/hr-case-management#dashboard': {
    name: 'Case Dashboard',
    capabilities: [
      { icon: Icons.zap,           label: 'Resolution rate',      query: 'What is the autonomous resolution rate, and how does the L1 and L2 split look?', accent: 'text-emerald-600' },
      { icon: Icons.inbox,         label: 'Volume by channel',    query: 'Break case volume down by intake channel: Teams, Email, Portal and ServiceNow.', accent: 'text-brand-cornflower' },
      { icon: Icons.clock,         label: 'SLA performance',      query: 'How are we performing against case SLAs, and which case types breach most?', accent: 'text-amber-600' },
      { icon: Icons.brain,         label: 'Policy triggers',      query: 'Which policies triggered the most escalations, and what does that tell us?', accent: 'text-brand-navy' },
    ],
  },
  '/hr-case-management#queue': {
    name: 'Live Queue',
    capabilities: [
      { icon: Icons.inbox,         label: 'What is open',         query: 'What is open in the live queue right now, and who owns each case?', accent: 'text-brand-cornflower' },
      { icon: Icons.alertCircle,   label: 'SLA breaches',         query: 'Which cases in this queue have breached their SLA?', accent: 'text-red-600' },
      { icon: Icons.gitBranch,     label: 'Escalated cases',      query: 'Which cases were escalated to the Auto Workbench, and why was each escalated?', accent: 'text-amber-600' },
      { icon: Icons.shield,        label: 'Next case to work',    query: 'Given SLA risk and case type, which case in this queue should be worked next?', accent: 'text-purple-600' },
    ],
  },
  '/hr-case-management#detail': {
    name: 'Case Detail',
    capabilities: [
      { icon: Icons.gitBranch,     label: 'Explain this case',    query: 'Explain this case: classification, pathway, the agents involved and where it stands.', accent: 'text-brand-cornflower' },
      { icon: Icons.shield,        label: 'Policies in play',     query: 'Which policies govern this case, and what does each one require?', accent: 'text-purple-600' },
      { icon: Icons.clock,         label: 'Audit trail',          query: 'Walk me through the audit trail on this case, action by action.', accent: 'text-brand-navy' },
      { icon: Icons.zap,           label: 'Recommended action',   query: 'What is the recommended next action on this case, and who should take it?', accent: 'text-emerald-600' },
    ],
  },

  // ── AI Policies ─────────────────────────────────────────────────────────
  '/ai/policies': {
    name: 'AI Policies',
    capabilities: [
      { icon: Icons.brain,         label: 'Create a policy',      query: 'Help me create a policy that automatically escalates any accommodation request to Legal within 2 hours.', accent: 'text-brand-cornflower' },
      { icon: Icons.fileText,      label: 'Active policies',      query: 'Show me all active AI policies and their categories.', accent: 'text-brand-navy' },
      { icon: Icons.shield,        label: 'Policy impact',        query: 'Which policies have the highest escalation rate?', accent: 'text-amber-600' },
      { icon: Icons.zap,           label: 'Retention policy',     query: 'Create a retention policy for Engineering employees above the flight-risk threshold.', accent: 'text-emerald-600' },
    ],
  },

  // ── AI Insights ─────────────────────────────────────────────────────────
  '/ai/insights': {
    name: 'AI Insights',
    capabilities: [
      { icon: Icons.lightbulb,     label: 'Top insights',         query: 'What are the most important insights the system has generated, and what is the evidence for each?', accent: 'text-amber-600' },
      { icon: Icons.layers,        label: 'Patterns detected',    query: 'Which patterns has the system detected across the H2R data?', accent: 'text-purple-600' },
      { icon: Icons.zap,           label: 'Recommended actions',  query: 'What actions are recommended right now, and which one has the biggest impact?', accent: 'text-emerald-600' },
      { icon: Icons.alertTriangle, label: 'Anomalies',            query: 'Show me any anomalies in the workforce data and explain what is unusual about each.', accent: 'text-red-600' },
    ],
  },
  '/ai/insights#summary': {
    name: 'Insights Summary',
    capabilities: [
      { icon: Icons.lightbulb,     label: 'Top insights',         query: 'Summarise the highest-severity insights and what each one is based on.', accent: 'text-amber-600' },
      { icon: Icons.trendingUp,    label: 'What changed',         query: 'What has changed in the workforce data recently that I should know about?', accent: 'text-emerald-600' },
      { icon: Icons.alertTriangle, label: 'Biggest risk',         query: 'What is the single biggest people risk in the data right now?', accent: 'text-red-600' },
      { icon: Icons.brain,         label: 'Evidence behind this', query: 'For the top insight on this page, show me the underlying data it was derived from.', accent: 'text-brand-navy' },
    ],
  },
  '/ai/insights#patterns': {
    name: 'Patterns',
    capabilities: [
      { icon: Icons.layers,        label: 'Patterns detected',    query: 'Which patterns has the system detected, and how strong is each one?', accent: 'text-purple-600' },
      { icon: Icons.building,      label: 'Departmental pattern', query: 'Are any of these patterns concentrated in one department?', accent: 'text-brand-navy' },
      { icon: Icons.trendingUp,    label: 'Attrition pattern',    query: 'Is there a pattern connecting engagement, tenure and attrition in this data?', accent: 'text-red-600' },
      { icon: Icons.clock,         label: 'Timing pattern',       query: 'Do separations or case volumes cluster at particular points in the year?', accent: 'text-amber-600' },
    ],
  },
  '/ai/insights#actions': {
    name: 'Recommended Actions',
    capabilities: [
      { icon: Icons.zap,           label: 'Highest impact',       query: 'Which recommended action has the highest impact, and what is the reasoning?', accent: 'text-emerald-600' },
      { icon: Icons.users,         label: 'Who is affected',      query: 'For the top recommended action, which employees or teams does it affect?', accent: 'text-brand-cornflower' },
      { icon: Icons.brain,         label: 'Turn into a policy',   query: 'Turn the top recommended action into an AI policy I can review.', accent: 'text-brand-navy' },
      { icon: Icons.clock,         label: 'Sequence the work',    query: 'Sequence these recommended actions for the next two weeks.', accent: 'text-amber-600' },
    ],
  },
  '/ai/insights#case-mgmt': {
    name: 'Case Management Insights',
    capabilities: [
      { icon: Icons.zap,           label: 'Autonomy rate',        query: 'What is the autonomous resolution rate, and where is it losing ground?', accent: 'text-emerald-600' },
      { icon: Icons.gitBranch,     label: 'Escalation drivers',   query: 'What is driving escalations from L1 to L2?', accent: 'text-purple-600' },
      { icon: Icons.clock,         label: 'SLA pressure',         query: 'Which case types are under the most SLA pressure?', accent: 'text-amber-600' },
      { icon: Icons.brain,         label: 'Close the gap',        query: 'What policy change would move the most cases from L2 to L1?', accent: 'text-brand-navy' },
    ],
  },

  // ── Agent Hub ───────────────────────────────────────────────────────────
  '/agent-hub': {
    name: 'Coworker Hub',
    capabilities: [
      { icon: Icons.users,         label: 'Coworker roster',      query: 'Show me the coworker roster with each one\'s status and health.', accent: 'text-brand-cornflower' },
      { icon: Icons.activity,      label: 'Agent load',           query: 'Which agents are carrying the most tasks right now?', accent: 'text-amber-600' },
      { icon: Icons.alertCircle,   label: 'Unhealthy agents',     query: 'Are any agents degraded or failing, and what is the impact?', accent: 'text-red-600' },
      { icon: Icons.network,       label: 'Who does what',        query: 'Which orchestrator owns which agents, and what does each one execute?', accent: 'text-brand-navy' },
    ],
  },
  '/agent-hub#map': {
    name: 'Agent Map',
    capabilities: [
      { icon: Icons.network,       label: 'Explain this map',     query: 'Explain this map: which coworker owns which skills, and what each one does.', accent: 'text-brand-navy' },
      { icon: Icons.users,         label: 'Busiest coworker',     query: 'Which coworker is carrying the most work right now?', accent: 'text-brand-cornflower' },
      { icon: Icons.alertCircle,   label: 'Weak links',           query: 'Where is this agent network most fragile, and what would fail first?', accent: 'text-red-600' },
      { icon: Icons.activity,      label: 'Recent activity',      query: 'What have these agents actually done recently?', accent: 'text-emerald-600' },
    ],
  },
  '/agent-hub#grid': {
    name: 'Coworkers',
    capabilities: [
      { icon: Icons.users,         label: 'Coworker roster',      query: 'List every coworker here with their status, owner and task count.', accent: 'text-brand-cornflower' },
      { icon: Icons.alertCircle,   label: 'Degraded agents',      query: 'Which agents are degraded or idle, and for how long?', accent: 'text-red-600' },
      { icon: Icons.activity,      label: 'Busiest agents',       query: 'Which agents have handled the most tasks, and what kind of work is it?', accent: 'text-amber-600' },
      { icon: Icons.shield,        label: 'Coverage check',       query: 'Is every H2R process covered by at least one healthy agent?', accent: 'text-purple-600' },
    ],
  },
  '/agent-hub#performance': {
    name: 'Agent Performance',
    capabilities: [
      { icon: Icons.trendingUp,    label: 'Top performers',       query: 'Which agents perform best, and on what measure?', accent: 'text-emerald-600' },
      { icon: Icons.clock,         label: 'Slowest agents',       query: 'Which agents take longest to complete a task, and why?', accent: 'text-amber-600' },
      { icon: Icons.alertTriangle, label: 'Failure rate',         query: 'Where are agent failures concentrated?', accent: 'text-red-600' },
      { icon: Icons.barChart,      label: 'Throughput trend',     query: 'How has agent throughput moved over the recorded period?', accent: 'text-brand-navy' },
    ],
  },

  // ── Data Manager ────────────────────────────────────────────────────────
  '/data-manager': {
    name: 'Data Manager',
    capabilities: [
      { icon: Icons.database,      label: 'What data is here',    query: 'What HR data does the Corporate Services Command Center hold, and which entities can you query?', accent: 'text-brand-cornflower' },
      { icon: Icons.activity,      label: 'Ingestion status',     query: 'What is the status of the connected data sources?', accent: 'text-emerald-600' },
      { icon: Icons.alertCircle,   label: 'Data quality gaps',    query: 'Where is the workforce data incomplete, and what does that block?', accent: 'text-red-600' },
      { icon: Icons.brain,         label: 'Knowledge coverage',   query: 'What does the knowledge base cover, and where are the gaps?', accent: 'text-brand-navy' },
    ],
  },

  // ── Admin ───────────────────────────────────────────────────────────────
  '/admin/audit': {
    name: 'Audit Log',
    capabilities: [
      { icon: Icons.shield,        label: 'Recent actions',       query: 'What has been recorded in the audit log recently, and by whom?', accent: 'text-brand-navy' },
      { icon: Icons.alertCircle,   label: 'Sensitive changes',    query: 'Show me the highest-sensitivity actions in the audit trail.', accent: 'text-red-600' },
      { icon: Icons.users,         label: 'Busiest actors',       query: 'Which users and agents generated the most audited actions?', accent: 'text-brand-cornflower' },
      { icon: Icons.clock,         label: 'Activity over time',   query: 'How has audited activity moved over the recorded period?', accent: 'text-amber-600' },
    ],
  },
  '/settings': {
    name: 'Settings',
    capabilities: [
      { icon: Icons.settings,      label: 'Explain this page',    query: 'What can I configure on this page, and what does each setting change?', accent: 'text-brand-navy' },
      { icon: Icons.shield,        label: 'Governance defaults',  query: 'Which governance defaults are in force across the Corporate Services Command Center?', accent: 'text-purple-600' },
      { icon: Icons.brain,         label: 'System status',        query: 'Give me a complete system status overview.', accent: 'text-emerald-600' },
      { icon: Icons.fileText,      label: 'Active policies',      query: 'Show me all active AI policies and their categories.', accent: 'text-brand-cornflower' },
    ],
  },
}

/**
 * The set used when the pathname matches nothing above.
 *
 * It stays deliberately broad. A chip that fires a question the current page has
 * no data for is worse than a generic one, because the answer then has to admit
 * the figure is not loaded.
 */
const DEFAULT_SET: CapabilitySet = {
  name: 'Corporate Services Command Center',
  capabilities: [
    { icon: Icons.gitBranch,     label: 'Classify HR query',      query: 'An employee wants to dispute a $250 paycheck discrepancy. Is this L1 or L2?', accent: 'text-brand-cornflower' },
    { icon: Icons.alertTriangle, label: 'Flight risks',           query: 'Who are the top flight risks and what are their risk factors?', accent: 'text-red-600' },
    { icon: Icons.activity,      label: 'System status',          query: 'Give me a complete system status overview.', accent: 'text-emerald-600' },
    { icon: Icons.brain,         label: 'Create a policy',        query: 'Help me create a new AI policy.', accent: 'text-brand-navy' },
  ],
}

/**
 * Resolve a page + section to its suggestion set.
 *
 * The page is resolved first (exact route, then longest matching route prefix),
 * and the section is then looked up under the page key that won. This is the
 * same resolution order the backend page registry uses, so a nested route such
 * as `/admin/audit/123` lands on the same view the AI Manager thinks the user is
 * on, and a section still resolves on a detail URL.
 */
function resolveSet(page: string, section: string | null): CapabilitySet {
  const route = page || '/'

  // Resolve the PAGE first, exactly as the backend registry does, then look for
  // the section under whichever page key won. Checking `route#section` against
  // the raw pathname first would lose the section on any nested route: the case
  // detail URL /hr-case-management/CASE-1042 never matches a `#queue` key on its
  // own, and the chips would silently fall back to the page-level set while the
  // AI Manager still believed the queue sub-view was open.
  const pageKey = resolvePageKey(route)

  if (section && pageKey) {
    const exact = CAPABILITY_SETS[`${pageKey}#${section}`]
    if (exact) return exact
  }

  return (pageKey ? CAPABILITY_SETS[pageKey] : undefined) ?? DEFAULT_SET
}

/** The page key this route belongs to: exact match, then longest prefix. */
function resolvePageKey(route: string): string | undefined {
  if (CAPABILITY_SETS[route]) return route

  let best: string | undefined
  let bestLength = 0
  for (const key of Object.keys(CAPABILITY_SETS)) {
    // '/' is skipped as a prefix because it matches everything; it is only ever
    // reached as the exact match above. Section keys are never prefix targets.
    if (key === '/' || key.includes('#')) continue
    // Match on a path SEGMENT boundary, not a bare string prefix, so a future
    // sibling route such as /workbench-archive cannot inherit the Operations Hub
    // chips just because its name starts the same way.
    if (route.startsWith(`${key}/`) && key.length > bestLength) {
      best = key
      bestLength = key.length
    }
  }

  return best
}

/**
 * The two chips every view gets.
 *
 * "About this view" exists because the AI used to answer a page question with a
 * generic module blurb naming all five Operations Hub tabs; it is the fastest
 * way for a user to prove the copilot knows which sub-view is open. The export
 * chip exists because the report download had no entry point in the UI at all.
 */
function universalCapabilities(name: string): Capability[] {
  return [
    {
      icon: Icons.helpCircle,
      label: 'About this view',
      query: `What is this page about? Explain what ${name} shows me and what the current figures are.`,
      accent: 'text-brand-cornflower',
    },
    {
      icon: Icons.download,
      label: 'Export as Word',
      query: `Export this page as a Word document: a ${name} report with the current figures.`,
      accent: 'text-purple-600',
    },
  ]
}

function getCapabilities(page: string, section: string | null): Capability[] {
  const set = resolveSet(page, section)
  return [...set.capabilities, ...universalCapabilities(set.name)]
}

// ── Component ──────────────────────────────────────────────────────────────

interface CapabilityBubblesProps {
  onSelect: (query: string) => void
}

export function CapabilityBubbles({ onSelect }: CapabilityBubblesProps) {
  const { currentPageContext, currentPageSection } = useAI()
  const capabilities = getCapabilities(currentPageContext, currentPageSection)

  return (
    <div className='flex flex-wrap justify-center gap-2 p-1'>
      {capabilities.map((cap, i) => {
        const Icon = cap.icon
        return (
          <motion.button
            // Keyed on the query rather than the index so switching sub-views
            // remounts the chips and replays the stagger, instead of animating
            // a Retention Radar label into a Separations one in place.
            key={cap.query}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.05, duration: 0.2, ease: 'easeOut' }}
            onClick={() => onSelect(cap.query)}
            className={cn(
              'flex items-center gap-2 px-3 py-2 rounded-xl',
              'bg-white border border-gray-200',
              'text-brand-navy',
              'shadow-sm',
              'transition-all duration-150',
              'hover:border-brand-cornflower/40 hover:shadow-md hover:bg-gray-50',
              'focus:outline-none focus:ring-2 focus:ring-brand-cornflower/40',
              'text-left'
            )}
          >
            <Icon className={cn('h-3.5 w-3.5 shrink-0', cap.accent ?? 'text-brand-cornflower')} strokeWidth={1.5} />
            <span className='text-xs font-medium'>{cap.label}</span>
          </motion.button>
        )
      })}
    </div>
  )
}
